import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  UserRound,
  LogIn,
  LogOut,
  Settings as SettingsIcon,
  Building2,
  Home,
  ChevronDown,
  ArrowLeftRight,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useRooms } from '../features/rooms/useRooms';
import { useActiveRoom } from '../features/rooms/RoomContext';
import { roomsAccessibleTo, sortRoomsByRecency } from '../features/rooms/roomModel';
import { useActiveTeam } from '../features/tenant/useActiveTeam';
import { isBuiltInTeam } from '../features/tenant/builtInTeam';
import { Button, Select, Spinner, type SelectOption } from '../ui';

/**
 * Top-bar team switcher: shows the active tenant team and lets a signed-in user
 * switch teams. "Switch team" clears the active team, which returns the app to
 * the tenant gate (the {@link TenantGate} re-renders the "Choose your team"
 * screen). Navigates to '/' first so we leave any bypass route (e.g.
 * /onboarding) and land on a gated route where the gate is shown. Renders
 * nothing until a team is active.
 */
function TeamSwitcher() {
  const navigate = useNavigate();
  const { activeTeam, clearActiveTeam } = useActiveTeam();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  if (!activeTeam) return null;

  const builtIn = isBuiltInTeam(activeTeam);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Active team: ${activeTeam.teamName}. Switch team`}
        className="flex h-8 max-w-[13rem] items-center gap-2 rounded-lg border border-surface-border bg-surface-1 px-2.5 text-sm text-zinc-300 transition-colors hover:text-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-accent-soft text-accent-fg">
          {builtIn ? (
            <Home className="h-3 w-3" aria-hidden="true" />
          ) : (
            <Building2 className="h-3 w-3" aria-hidden="true" />
          )}
        </span>
        <span className="truncate font-medium text-zinc-100">
          {activeTeam.teamName}
        </span>
        <ChevronDown
          className="h-3.5 w-3.5 shrink-0 text-zinc-500"
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          role="menu"
          className="animate-fade-in absolute right-0 z-40 mt-2 w-60 overflow-hidden rounded-xl border border-surface-border bg-surface-2 shadow-pop"
        >
          <div className="border-b border-surface-border px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-zinc-500">
              Current team
            </p>
            <p className="mt-0.5 truncate text-sm font-medium text-zinc-100">
              {activeTeam.teamName}
            </p>
            {!builtIn && (
              <p className="mt-0.5 truncate font-mono text-[11px] text-zinc-500">
                {activeTeam.bindleId}
              </p>
            )}
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/');
              clearActiveTeam();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-zinc-300 transition-colors hover:bg-surface-3 hover:text-zinc-100 focus:outline-none focus-visible:bg-surface-3"
          >
            <ArrowLeftRight className="h-4 w-4 text-zinc-500" aria-hidden="true" />
            Switch team
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Top-bar room switcher: lets a signed-in user change the active workspace room
 * from anywhere. Bound to the active-room context (persisted) and navigates to
 * the chosen room. Renders nothing until the user has at least one room (the
 * first-run flow creates one). Reaches data only through hooks — strict layering.
 */
function RoomSwitcher() {
  const navigate = useNavigate();
  const { summaries, loading } = useRooms();
  const { activeRoomId, setActiveRoomId } = useActiveRoom();
  const { identity } = useAuth();

  if (loading && summaries.length === 0) return null;
  if (summaries.length === 0) return null;

  // Prefer rooms the signed-in user can access (created OR is a member of).
  // SAFE FALLBACK: if the user is in none, show every room so no one is locked
  // out. Always keep the active room selectable so the control never goes blank.
  const accessible = roomsAccessibleTo(summaries, identity?.alias ?? '');
  const visible = accessible.length > 0 ? [...accessible] : summaries;
  if (activeRoomId && !visible.some((r) => r.id === activeRoomId)) {
    const active = summaries.find((r) => r.id === activeRoomId);
    if (active) visible.push(active);
  }

  const options: SelectOption[] = sortRoomsByRecency(visible).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  return (
    <Select
      aria-label="Active room"
      placeholder="Select room"
      className="w-52"
      value={activeRoomId ?? ''}
      options={options}
      onChange={(e) => {
        const id = e.target.value;
        if (!id) return;
        setActiveRoomId(id);
        navigate(`/rooms/${encodeURIComponent(id)}`);
      }}
    />
  );
}

/**
 * Top bar: auth affordance. Shows the signed-in Midway identity (name/alias)
 * with a sign-out control, or a Sign-in button when signed out. Also links to
 * the Settings/Connection page.
 */
export function TopBar() {
  const { status, identity, signIn, signOut } = useAuth();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-end gap-3 border-b border-surface-border bg-surface-0/80 px-6 backdrop-blur">
      {status === 'authenticated' && identity && <TeamSwitcher />}
      {status === 'authenticated' && identity && <RoomSwitcher />}

      <Link
        to="/settings"
        aria-label="Settings & connection"
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-surface-border bg-surface-1 text-zinc-400 transition-colors hover:text-zinc-200"
      >
        <SettingsIcon className="h-4 w-4" aria-hidden="true" />
      </Link>

      {status === 'loading' ? (
        <div className="flex h-8 items-center rounded-xl border border-surface-border bg-surface-1 px-3">
          <Spinner label="Checking session" />
        </div>
      ) : status === 'authenticated' && identity ? (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2.5 rounded-xl border border-surface-border bg-surface-1 px-3 py-1.5">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-soft text-accent-fg">
              <UserRound className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <span className="text-sm text-zinc-300">
              <span className="font-medium text-zinc-100">{identity.name}</span>
              <span className="ml-1.5 text-zinc-500">@{identity.alias}</span>
            </span>
          </div>
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<LogOut className="h-4 w-4" />}
            onClick={signOut}
            aria-label="Sign out"
          />
        </div>
      ) : (
        <Button
          size="sm"
          leftIcon={<LogIn className="h-4 w-4" />}
          onClick={signIn}
        >
          Sign in
        </Button>
      )}
    </header>
  );
}
